import type { ApiResponse, Auth, User } from "@/types";

import { BaseApi } from "../base.api";
import type {
  ChangePasswordData,
  EmailData,
  LoginData,
  RefreshData,
  ResetPasswordData,
  Session,
  UpdateProfileData,
} from "./types";

export * from "./types";

/**
 * Auth API — backend'dagi `AuthController` ning ko'zgusi (`/api/v1/auth`).
 *
 * Javob interceptori HTTP javobini backend'ning muvaffaqiyat konvertigacha
 * ochib beradi, shuning uchun har bir metod `ApiResponse<T>` ga yechiladi
 * ({ success, message, data, timestamp }). Ma'lumot uchun `res.data` ni,
 * odamga aytiladigan xabar uchun `res.message` ni o'qiysiz.
 */
export class AuthApi extends BaseApi {
  private readonly baseUrl: string = "/auth";

  // ─── Kirish ────────────────────────────────────────────────────────────────
  // Ro'yxatdan o'tish, Google orqali kirish va email tasdiqlash oqimi bu
  // yerda ATAYLAB yo'q: xodim hisobini faqat mavjud admin yaratadi, panelda
  // "ro'yxatdan o'tish" tugmasi bo'lishi mumkin emas.

  login(data: LoginData) {
    return this.request.post<Auth, ApiResponse<Auth>>(`${this.baseUrl}/login`, data);
  }

  // ─── Parolni tiklash ───────────────────────────────────────────────────────

  forgotPassword(data: EmailData) {
    return this.request.post<null, ApiResponse<null>>(`${this.baseUrl}/forgot-password`, data);
  }

  resetPassword(data: ResetPasswordData) {
    return this.request.post<null, ApiResponse<null>>(`${this.baseUrl}/reset-password`, data);
  }

  changePassword(data: ChangePasswordData) {
    return this.request.post<null, ApiResponse<null>>(`${this.baseUrl}/change-password`, data);
  }

  // ─── Tokenlar / sessiyalar ─────────────────────────────────────────────────

  refresh(data: RefreshData) {
    return this.request.post<Auth, ApiResponse<Auth>>(`${this.baseUrl}/refresh`, data);
  }

  logout(data: RefreshData) {
    return this.request.post<null, ApiResponse<null>>(`${this.baseUrl}/logout`, data);
  }

  /** Foydalanuvchining hamma sessiyasini bekor qiladi (hamma qurilma chiqadi). */
  logoutAll() {
    return this.request.post<null, ApiResponse<null>>(`${this.baseUrl}/logout-all`);
  }

  me() {
    return this.request.get<User, ApiResponse<User>>(`${this.baseUrl}/me`);
  }

  /** O'z profilini yangilash — backend'dagi `PATCH /profile`. */
  updateProfile(data: UpdateProfileData) {
    return this.request.patch<User, ApiResponse<User>>("/profile", data);
  }

  // ─── Faol sessiyalar (qurilmalar) ──────────────────────────────────────────

  /**
   * Joriy foydalanuvchining faol sessiyalari. Joriy refresh token
   * `x-refresh-token` sarlavhasida yuboriladi — backend qaysi sessiya shu
   * qurilmaniki ekanini shundan biladi.
   */
  listSessions(currentRefreshToken?: string) {
    return this.request.get<Session[], ApiResponse<Session[]>>(`${this.baseUrl}/sessions`, {
      headers: currentRefreshToken ? { "x-refresh-token": currentRefreshToken } : undefined,
    });
  }

  /** Bitta sessiyani id bo'yicha bekor qiladi (o'sha qurilma chiqadi). */
  revokeSession(id: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.baseUrl}/sessions/${id}`);
  }
}

export const authApi = new AuthApi();
